import { manuscriptSelectionText } from "../manuscriptSelectionText";
/**
 * Persisted selection ("fake highlight").
 *
 * The manuscript editor needs the user's text selection to stay visible when
 * focus moves to other inputs (chat composer, rewrite panel, etc.). The
 * browser only paints the native selection in the focused element, so when
 * the editor blurs with a non-empty selection we paint an inline decoration
 * over the same range, styled to approximate `::selection` (both use
 * `var(--text-highlight)`, with a small paint-only spread to better mimic
 * the native selection line box).
 *
 * ProseMirror's `state.selection` itself does not collapse on blur — this
 * plugin only solves the *painting* problem, plus exposes the range so
 * consumers (rewrite, chat) can treat it as the selection of record via
 * `getEffectiveSelection()`.
 *
 * Lifecycle: persist on editor blur (non-empty selection), clear on focus.
 * Clearing happens on focus rather than on `selectionUpdate` because remote
 * yjs transactions remap the selection while blurred and emit
 * `selectionUpdate`, which must NOT dismiss the highlight. The decoration and
 * the stored range both map through `tr.mapping`, so collaborative edits keep
 * the highlight glued to the original text.
 */

import { Extension } from "@tiptap/core";
import type { Editor as TipTapEditor } from "@tiptap/core";
import { Plugin, PluginKey, NodeSelection } from "@tiptap/pm/state";
import type { EditorState, Transaction } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export const persistedSelectionPluginKey = new PluginKey("persistedSelection");
export const PERSISTED_SELECTION_CLASS = "sudo-persisted-selection";

type PersistedRange = { from: number; to: number };
type SelectionKind = "text" | "node";

interface PersistedSelectionState {
  decorations: DecorationSet;
  range: PersistedRange | null;
  kind: SelectionKind | null;
}

type PersistMeta =
  | { type: "persist"; from: number; to: number; kind: SelectionKind }
  | { type: "clear" };

const EMPTY_STATE: PersistedSelectionState = {
  decorations: DecorationSet.empty,
  range: null,
  kind: null,
};

function buildState(tr: Transaction, meta: Extract<PersistMeta, { type: "persist" }>): PersistedSelectionState {
  const { from, to, kind } = meta;
  if (!(Number.isFinite(from) && Number.isFinite(to)) || from >= to) {
    return EMPTY_STATE;
  }
  const decoration =
    kind === "node"
      ? Decoration.node(from, to, { class: PERSISTED_SELECTION_CLASS })
      : Decoration.inline(from, to, { class: PERSISTED_SELECTION_CLASS });
  return {
    decorations: DecorationSet.create(tr.doc, [decoration]),
    range: { from, to },
    kind,
  };
}

const persistedSelectionPlugin = new Plugin<PersistedSelectionState>({
  key: persistedSelectionPluginKey,
  state: {
    init() {
      return EMPTY_STATE;
    },
    apply(tr, value) {
      const meta = tr.getMeta(persistedSelectionPluginKey) as PersistMeta | undefined;
      if (meta?.type === "clear") {
        return EMPTY_STATE;
      }
      if (meta?.type === "persist") {
        return buildState(tr, meta);
      }
      if (!value.range || !tr.docChanged) {
        return value;
      }
      // Keep the highlight glued to its text through collab/local edits.
      const from = tr.mapping.map(value.range.from, 1);
      const to = tr.mapping.map(value.range.to, -1);
      if (from >= to) {
        // The selected text was deleted out from under us.
        return EMPTY_STATE;
      }
      const decorations = value.decorations.map(tr.mapping, tr.doc);
      if (decorations.find().length === 0) {
        // The decoration is the visible source of truth. Node decorations can
        // be dropped by a ReplaceStep covering their node even when the
        // boundary-mapped range survives — never keep an invisible
        // "selection of record".
        return EMPTY_STATE;
      }
      return {
        decorations,
        range: { from, to },
        kind: value.kind,
      };
    },
  },
  props: {
    decorations(state) {
      return persistedSelectionPluginKey.getState(state)?.decorations;
    },
  },
});

export function getPersistedSelectionRange(state: EditorState): PersistedRange | null {
  return (persistedSelectionPluginKey.getState(state) as PersistedSelectionState | undefined)?.range ?? null;
}

export interface EffectiveSelection {
  from: number;
  to: number;
  text: string;
  isPersisted: boolean;
}

/**
 * The selection of record for features that act on "what the user has
 * highlighted": the persisted (fake) highlight wins when present, otherwise
 * the live non-empty ProseMirror selection. Returns null when there is no
 * highlight of either kind.
 */
export function getEffectiveSelection(editor: Pick<TipTapEditor, "state"> | null | undefined): EffectiveSelection | null {
  const state = editor?.state;
  if (!state) return null;

  const persisted = getPersistedSelectionRange(state);
  if (persisted) {
    return {
      from: persisted.from,
      to: persisted.to,
      text: manuscriptSelectionText(state.doc, persisted.from, persisted.to),
      isPersisted: true,
    };
  }

  const { from, to, empty } = state.selection;
  if (empty) return null;
  return {
    from,
    to,
    text: manuscriptSelectionText(state.doc, from, to),
    isPersisted: false,
  };
}

function dispatchMeta(editor: TipTapEditor, meta: PersistMeta) {
  const tr = editor.state.tr.setMeta(persistedSelectionPluginKey, meta).setMeta("addToHistory", false);
  editor.view.dispatch(tr);
}

/** Clear the fake highlight when the user moves the caret or makes a new local selection. */
export function shouldClearPersistedSelectionOnLocalSelectionChange(
  transaction: Pick<Transaction, "selectionSet" | "docChanged">,
  persisted: PersistedRange | null,
  selection: { from: number; to: number; empty: boolean },
): boolean {
  if (!persisted) return false;
  if (!transaction.selectionSet || transaction.docChanged) return false;
  if (selection.empty) return true;
  return selection.from !== persisted.from || selection.to !== persisted.to;
}

function clearPersistedSelectionOnLocalSelectionChange(editor: TipTapEditor, transaction: Transaction): void {
  const persisted = getPersistedSelectionRange(editor.state);
  if (
    !shouldClearPersistedSelectionOnLocalSelectionChange(transaction, persisted, editor.state.selection)
  ) {
    return;
  }
  dispatchMeta(editor, { type: "clear" });
}

/** Paint the fake highlight over an explicit range (e.g. agent jump-to-passage). */
export function persistSelectionRange(
  editor: TipTapEditor,
  from: number,
  to: number,
  kind: SelectionKind = "text",
): void {
  if (!(Number.isFinite(from) && Number.isFinite(to)) || from >= to) return;
  dispatchMeta(editor, { type: "persist", from: Math.floor(from), to: Math.floor(to), kind });
}

function persistFromSelection(editor: TipTapEditor): boolean {
  const selection = editor.state.selection;
  if (selection.empty) return false;
  dispatchMeta(editor, {
    type: "persist",
    from: selection.from,
    to: selection.to,
    kind: selection instanceof NodeSelection ? "node" : "text",
  });
  return true;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    persistedSelection: {
      persistCurrentSelection: () => ReturnType;
      clearPersistedSelection: () => ReturnType;
    };
  }
}

export const PersistedSelectionExtension = Extension.create({
  name: "persistedSelection",

  addProseMirrorPlugins() {
    return [persistedSelectionPlugin];
  },

  addCommands() {
    return {
      persistCurrentSelection:
        () =>
        ({ editor }: { editor: TipTapEditor }) =>
          persistFromSelection(editor),
      clearPersistedSelection:
        () =>
        ({ editor }: { editor: TipTapEditor }) => {
          if (!getPersistedSelectionRange(editor.state)) return true;
          dispatchMeta(editor, { type: "clear" });
          return true;
        },
    };
  },

  onBlur() {
    // Quick Edit collapses the selection before its input steals focus, so
    // its own highlight flow never double-paints here (blur sees empty).
    persistFromSelection(this.editor);
  },

  onFocus() {
    if (getPersistedSelectionRange(this.editor.state)) {
      dispatchMeta(this.editor, { type: "clear" });
      // Keep the live PM selection on the same range so chat highlight sync can
      // still read the passage after focus returns from the composer.
    }
  },

  onSelectionUpdate({ editor, transaction }) {
    clearPersistedSelectionOnLocalSelectionChange(editor, transaction);
  },
});
